import React, { useEffect, useState } from "react";
import { Flame, Droplet, Timer, Pill, Plus, Shirt } from "lucide-react";
import { toast } from "sonner";
import { api, moodColor, palette } from "@/lib/api";
import { Bubba, Ring } from "@/lib/Bubba";
import { Card } from "@/pages/bitepal/Home";

export default function Today() {
  const [d, setD] = useState(null);
  const load = async () => { try { setD(await api("/home/summary")); } catch {} };
  useEffect(() => { load(); }, []);

  const logWater = async () => { try { await api("/food/water", { method: "POST", body: { ml: 250 } }); load(); } catch {} };
  const toggleFast = async () => {
    try { if (d.fasting.active) await api("/fasting/end", { method: "POST" }); else await api("/fasting/start", { method: "POST", body: { pattern: "16:8" } }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (!d) return <div className="text-slate-500">Loading…</div>;
  const p = d.pet, eq = d.equation, m = d.macros, w = d.water;
  const accent = moodColor[p.mood] || palette.emerald;
  const xpPct = p.xp_for_next ? (p.xp_in_level / p.xp_for_next) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div><h1 className="text-3xl font-extrabold">Hi {d.user.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="text-slate-500 text-sm">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p></div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E3D9C8]"><Flame size={16} className="text-[#F97316]" /><span className="font-bold text-sm">Lv {p.level}</span></div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Pet */}
        <Card className="flex flex-col items-center" >
          <div className="bg-[#ECE4D6] border rounded-2xl px-4 py-3 text-center text-sm text-slate-800" style={{ borderColor: accent + "55" }}>{p.dialogue_bubble}</div>
          <Bubba mood={p.mood} cosmetic={p.active_cosmetic} size={150} />
          <span className="text-sm font-bold" style={{ color: accent }}>{p.mood}</span>
          <div className="w-full flex items-center gap-2 mt-3">
            <span className="text-xs font-bold text-[#FCD34D]">XP</span>
            <div className="flex-1 h-2 rounded-full bg-[#EAE1D2] overflow-hidden"><div className="h-full rounded-full bg-[#FCD34D]" style={{ width: `${xpPct}%` }} /></div>
            <span className="text-xs text-slate-500">{p.xp_in_level}/{p.xp_for_next}</span>
          </div>
        </Card>

        {/* Calorie + macros */}
        <div className="flex flex-col gap-4">
          <Card>
            <div className="flex justify-between items-end">
              <span className="text-sm text-slate-500 font-semibold">Calories remaining</span>
              <span className="text-4xl font-extrabold" style={{ color: eq.remaining < 0 ? palette.orange : palette.emerald }}>{eq.remaining}</span>
            </div>
            <div className="flex items-center mt-3 text-center">
              {[["Goal", eq.goal], ["−", null], ["Food", eq.food], ["+", null], ["Exercise", eq.exercise]].map(([l, v], i) =>
                v === null ? <span key={i} className="px-2 text-slate-500 font-bold">{l}</span>
                  : <div key={i} className="flex-1"><p className="text-lg font-bold">{v}</p><p className="text-[11px] text-slate-500">{l}</p></div>)}
            </div>
          </Card>
          <Card><div className="flex justify-around">
            <Ring value={m.protein.value} target={m.protein.target} color={palette.emerald} label="Protein" size={96} />
            <Ring value={m.carbs.value} target={m.carbs.target} color={palette.blue} label="Carbs" size={96} />
            <Ring value={m.fat.value} target={m.fat.target} color="#F59E0B" label="Fat" size={96} />
          </div></Card>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <Card>
          <div className="flex justify-between items-center"><span className="flex items-center gap-2 font-bold"><Droplet size={18} className="text-[#22D3EE]" /> Water</span><span className="text-sm text-slate-500">{w.value}/{w.target} ml</span></div>
          <div className="h-2.5 rounded-full bg-[#EAE1D2] mt-3 overflow-hidden"><div className="h-full rounded-full bg-[#22D3EE]" style={{ width: `${Math.min(100, (w.value / w.target) * 100)}%` }} /></div>
          <button onClick={logWater} className="w-full mt-3 py-2 rounded-full bg-[#22D3EE]/15 text-[#22D3EE] font-bold text-sm flex items-center justify-center gap-1"><Plus size={16} /> Add 250 ml</button>
        </Card>
        <Card>
          <div className="flex justify-between items-center"><span className="flex items-center gap-2 font-bold"><Timer size={18} className="text-[#F97316]" /> Fasting</span>
            <span className="text-sm text-slate-500">{d.fasting.active ? `${d.fasting.pattern}` : "Eating window"}</span></div>
          {d.fasting.active && <div className="h-2.5 rounded-full bg-[#EAE1D2] mt-3 overflow-hidden"><div className="h-full rounded-full bg-[#F97316]" style={{ width: `${(d.fasting.progress || 0) * 100}%` }} /></div>}
          <button onClick={toggleFast} className={`w-full mt-3 py-2 rounded-full font-bold text-sm border border-[#F97316]/50 ${d.fasting.active ? "text-[#F97316]" : "bg-[#F97316] text-[#F5F0E8]"}`}>{d.fasting.active ? "End fast" : "Start 16:8 fast"}</button>
        </Card>
        <Card>
          <div className="flex items-center gap-2 font-bold"><Pill size={18} className="text-[#651F33]" /> Medications</div>
          <p className="text-sm text-slate-500 mt-3">{d.meds.total === 0 ? "No meds scheduled" : d.meds.pending === 0 ? `All ${d.meds.total} doses taken 🎉` : `${d.meds.pending} of ${d.meds.total} doses due`}</p>
        </Card>
      </div>
    </div>
  );
}
